import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, ArrowLeft, X, Home } from 'lucide-react';

/**
 * Customizable BackButton Component
 * 
 * @param {Object} props - Component props
 * @param {string} [props.text="Go Back"] - Button text
 * @param {string} [props.variant="default"] - Button style variant
 * @param {string} [props.size="md"] - Button size
 * @param {string} [props.fallback] - Fallback route if no history
 * @param {Function} [props.onClick] - Custom click handler
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles
 * @param {boolean} [props.disabled=false] - Disable button
 * @param {string} [props.iconType="arrow"] - Icon type: arrow, chevron, x, home, none
 * @param {string} [props.position="left"] - Icon position: left, right
 * @param {boolean} [props.showIcon=true] - Show/hide icon
 */
const BackButton = ({
    text = "Go Back",
    variant = "default",
    size = "md",
    fallback = "/",
    onClick,
    className = "",
    style = {},
    disabled = false,
    iconType = "arrow",
    position = "left",
    showIcon = true,
    ...props
}) => {
    const { companySlug } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    const safeFallback = fallback || `/${companySlug}`;

    // Default back functionality
    const handleBack = () => {
        const from = location.state?.from;

        if (from && from !== location.pathname) {
            navigate(from, { replace: true });
        } else {
            navigate(safeFallback, { replace: true });
        }
    };

    // Icon components mapping
    const iconComponents = {
        arrow: ArrowLeft,
        chevron: ChevronLeft,
        x: X,
        home: Home,
        none: null
    };

    const IconComponent = iconComponents[iconType];

    // Base styles for different variants
    const variantStyles = {
        default: "bg-blue-500 hover:bg-blue-600 text-white border-blue-500",
        secondary: "bg-gray-500 hover:bg-gray-600 text-white border-gray-500",
        outline: "bg-transparent hover:bg-blue-50 text-blue-500 border-blue-500 border",
        ghost: "bg-transparent hover:bg-gray-100 text-gray-700 border-transparent",
        danger: "bg-red-500 hover:bg-red-600 text-white border-red-500",
        success: "bg-green-500 hover:bg-green-600 text-white border-green-500",
        warning: "bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500",
        dark: "bg-gray-800 hover:bg-gray-900 text-white border-gray-800",
        light: "bg-white hover:bg-gray-50 text-gray-800 border-gray-300 border",
        minimal: "bg-transparent hover:bg-gray-50 text-gray-600 border-transparent p-1"
    };

    // Size styles
    const sizeStyles = {
        xs: "px-2 py-1 text-xs",
        sm: "px-3 py-1.5 text-sm",
        md: "px-4 py-2 text-base",
        lg: "px-6 py-3 text-lg",
        xl: "px-8 py-4 text-xl"
    };

    // Icon sizes
    const iconSizes = {
        xs: 12,
        sm: 14,
        md: 16,
        lg: 20,
        xl: 24
    };

    // Combine all styles
    const buttonClass = [
        "inline-flex items-center justify-center",
        "rounded-md font-medium transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "active:scale-95",
        variantStyles[variant] || variantStyles.default,
        sizeStyles[size] || sizeStyles.md,
        className
    ].join(" ");

    return (
        <button
            type="button"
            className={buttonClass}
            style={style}
            onClick={handleBack}
            onTouchEnd={(e) => { e.preventDefault(); handleBack() }}
            disabled={disabled}
            {...props}
        >
            {showIcon && IconComponent && position === "left" && (
                <IconComponent
                    size={iconSizes[size] || 16}
                    className={text ? "mr-2" : ""}
                />
            )}

            {text}

            {showIcon && IconComponent && position === "right" && (
                <IconComponent
                    size={iconSizes[size] || 16}
                    className={text ? "ml-2" : ""}
                />
            )}
        </button>
    );
};

export default BackButton;

// Export preset variants for quick use
export const BackButtonOutline = (props) => <BackButton variant="outline" {...props} />;
export const BackButtonGhost = (props) => <BackButton variant="ghost" {...props} />;
export const BackButtonDanger = (props) => <BackButton variant="danger" {...props} />;
export const BackButtonSuccess = (props) => <BackButton variant="success" {...props} />;
export const BackButtonSecondary = (props) => <BackButton variant="secondary" {...props} />;

// Export size variants
export const BackButtonSmall = (props) => <BackButton size="sm" {...props} />;
export const BackButtonLarge = (props) => <BackButton size="lg" {...props} />;
export const BackButtonXL = (props) => <BackButton size="xl" {...props} />;

// Export utility variants
export const BackButtonMinimal = (props) => (
    <BackButton
        variant="minimal"
        text=""
        iconType="arrow"
        size="sm"
        className="rounded-full w-8 h-8 p-1"
        {...props}
    />
);

export const BackButtonIconOnly = (props) => (
    <BackButton
        text=""
        size="sm"
        iconType="arrow"
        className="w-10 h-10 p-2 rounded-full"
        {...props}
    />
);

export const BackButtonChevron = (props) => (
    <BackButton
        iconType="chevron"
        {...props}
    />
);

export const BackButtonClose = (props) => (
    <BackButton
        text="Close"
        iconType="x"
        variant="ghost"
        {...props}
    />
);